import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { razborKey } from '../obrashcheniya/hooks';
import { fetchRazbor } from '../obrashcheniya/api';
import {
  fetchDoskaLidov,
  fetchDoskaZapisey,
  fetchIstoriya,
  slovoOtkaza,
  type DoskaLidovFiltr,
  type DoskaZapiseyFiltr,
  type RazborLida,
} from './api';

/**
 * ⛔ ЖИВОСТЬ — ОПРОСОМ, ТЕМ ЖЕ ТАКТОМ, ЧТО ОЧЕРЕДЬ ОБРАЩЕНИЙ (`s603`): доска
 * лидов — та же очередь, разложенная по колонкам; разный такт развёл бы число
 * «новых» на доске и в баре шапки.
 */
const DOSKA_LIDOV_MS = 5_000;

/** Доска записей меняется реже — ходы журнала; полминуты достаточно. */
const DOSKA_ZAPISEY_MS = 30_000;

export const DOSKA_LIDOV_KEY = ['doski', 'lidy'] as const;
export const DOSKA_ZAPISEY_KEY = ['doski', 'zapisi'] as const;

export function useDoskaLidov(f: DoskaLidovFiltr, enabled = true) {
  return useQuery({
    queryKey: [...DOSKA_LIDOV_KEY, f],
    queryFn: () => fetchDoskaLidov(f),
    enabled,
    refetchInterval: DOSKA_LIDOV_MS,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
    retry: false,
  });
}

export function useDoskaZapisey(f: DoskaZapiseyFiltr, enabled = true) {
  return useQuery({
    queryKey: [...DOSKA_ZAPISEY_KEY, f],
    queryFn: () => fetchDoskaZapisey(f),
    enabled,
    refetchInterval: DOSKA_ZAPISEY_MS,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
    retry: false,
  });
}

/** Разбор лида — тот же ключ, что у «Обращений»: одна запись кэша на обращение. */
export function useRazborLida(id: number | null) {
  return useQuery({
    queryKey: razborKey(id ?? 0),
    queryFn: () => fetchRazbor(id as number) as Promise<RazborLida>,
    enabled: id !== null,
    refetchInterval: DOSKA_LIDOV_MS,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
    retry: false,
  }) as ReturnType<typeof useQuery<RazborLida>>;
}

export function useIstoriya(id: number | null, vkladka: string) {
  return useQuery({
    queryKey: ['doski', 'istoriya', id, vkladka],
    queryFn: () => fetchIstoriya(id as number, vkladka),
    enabled: id !== null,
    retry: false,
  });
}

/**
 * Ход карточки лида: ответ сервера кладётся в кэш разбора, доска и очередь
 * перезапрашиваются. Отказ говорит СЛОВАМИ сервера (422 `{message}`).
 */
export function useHodLida(id: number | null) {
  const qc = useQueryClient();
  const [idyot, setIdyot] = useState(false);
  const [otkaz, setOtkaz] = useState<string | null>(null);

  const vypolnit = useCallback(
    async (fn: () => Promise<RazborLida>): Promise<RazborLida | null> => {
      if (id === null) return null;
      setIdyot(true);
      setOtkaz(null);
      try {
        const svezhiy = await fn();
        qc.setQueryData(razborKey(id), svezhiy);
        void qc.invalidateQueries({ queryKey: DOSKA_LIDOV_KEY });
        void qc.invalidateQueries({ queryKey: ['obrashcheniya'] });

        return svezhiy;
      } catch (e) {
        setOtkaz(slovoOtkaza(e));

        return null;
      } finally {
        setIdyot(false);
      }
    },
    [id, qc],
  );

  return { vypolnit, idyot, otkaz, sbrositOtkaz: () => setOtkaz(null), setOtkaz };
}
